import type { GroupDetail, GroupItineraryItem, GroupPayload } from '../types/groups';

/**
 * The full group update body, rebuilt from the group already on screen.
 *
 * The update endpoint replaces the record rather than patching it, so every
 * GroupPayload field has to appear here: one that is added to the type but not
 * to this function is silently blanked on the next save. That used to be a
 * single-caller risk on the group page; it is now shared by the hero image and
 * the concierge instance code, so an omission blanks the group from two places.
 */
export function groupToPayload(group: GroupDetail, overrides: Partial<GroupPayload> = {}): GroupPayload {
  return {
    slug: group.slug,
    title: group.title,
    subtitle: group.subtitle || '',
    locationName: group.locationName || '',
    dateStart: group.dateStart ? group.dateStart.slice(0, 10) : undefined,
    dateEnd: group.dateEnd ? group.dateEnd.slice(0, 10) : undefined,
    timeZone: group.timeZone || undefined,
    heroImageUrl: group.heroImageUrl || '',
    story: group.story || '',
    externalRsvpUrl: group.externalRsvpUrl || '',
    externalRsvpLabel: group.externalRsvpLabel || '',
    accessMode: group.accessMode || 'invite_only',
    published: group.published,
    conciergeInstanceCode: group.conciergeInstanceCode || null,
    ...overrides,
  };
}

/**
 * The full itinerary upsert body, rebuilt from the tile already on screen.
 *
 * The endpoint is an upsert, but it is not uniformly a patch — the UPDATE
 * splits its columns two ways and the split is not guessable:
 *
 *   COALESCE(@x, x)  ends_at, time_zone, status, audience, and every place
 *                    snapshot field — omitting them preserves what is stored
 *   = @x             starts_at, title, description, location_name, latitude,
 *                    longitude, cta_label, cta_url, display_order — omitting
 *                    them WRITES NULL, or 0 for display_order
 *
 * So a body meaning "just publish this one" blanks the tile's start time and
 * its call-to-action, and drops it to the top of the list. Every write that
 * targets an existing tile goes through here, and anything added to the second
 * group above has to be added here too.
 */
export function itineraryToPayload(
  item: GroupItineraryItem,
  overrides: Record<string, unknown> = {},
): Record<string, unknown> {
  return {
    id: item.id,
    startsAt: item.startsAt || undefined,
    endsAt: item.endsAt || undefined,
    timeZone: item.timeZone || undefined,
    title: item.title,
    description: item.description ?? '',
    locationName: item.locationName ?? '',
    latitude: item.latitude ?? undefined,
    longitude: item.longitude ?? undefined,
    ctaLabel: item.ctaLabel ?? undefined,
    ctaUrl: item.ctaUrl ?? undefined,
    displayOrder: item.displayOrder ?? 0,
    ...overrides,
  };
}
